/**
 * Sidebar Navigation Component
 *
 * Main navigation sidebar with links to all app sections.
 * Uses NavLink for active state styling.
 * Footer shows a profile dropdown with settings, theme toggle, and logout.
 */

import { useAIChat } from '@/features/ai-chat';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { NotificationBell } from '@/features/notifications';
import { PREF_KEYS } from '@/shared/constants';
import { useUpdatePreference } from '@/shared/hooks/use-user-preferences';
import { cn } from '@/shared/utils';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTheme } from '../context/theme-provider';
import { Icon, IconKey } from '../icon';
import { Logo } from '../logo';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { SettingsDialog } from './settings-dialog';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: IconKey.Dashboard },
  { to: '/accounts', label: 'Accounts', icon: IconKey.Accounts },
  { to: '/transactions', label: 'Transactions', icon: IconKey.Transactions },
  { to: '/categories', label: 'Categories', icon: IconKey.Categories },
  { to: '/budgets', label: 'Budgets', icon: IconKey.Budgets },
  { to: '/goals', label: 'Goals', icon: IconKey.Goals },
  { to: '/reports', label: 'Reports', icon: IconKey.BarChart3 },
  { to: '/subscriptions', label: 'Recurring', icon: IconKey.Subscriptions },
  { to: '/settle-up', label: 'Settle Up', icon: IconKey.SettleUp },
  { to: '/tools', label: 'Tools', icon: IconKey.Wrench },
  { to: '/audit-log', label: 'Audit Log', icon: IconKey.History }
];

export function Sidebar() {
  const { logout, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const updateThemePref = useUpdatePreference(PREF_KEYS.THEME);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { isOpen: chatOpen, openChat, activeConversationId, startNewConversation } = useAIChat();

  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const initials = user?.name?.charAt(0).toUpperCase() ?? '?';

  return (
    <aside className='w-64 bg-transparent flex flex-col my-3'>
      <div className='flex items-center justify-start pl-5 gap-1 mb-3'>
        <Logo />
        <h1 className='text-5xl font-dafoe text-transparent bg-linear-to-r from-accent to-accent-foreground bg-clip-text p-3'>
          Tally
        </h1>
      </div>
      <nav className='flex-1 py-2 px-3 ml-2'>
        <ul className='space-y-1'>
          {navItems.map((item) => (
            <li key={item.to} className='group'>
              <NavLink
                to={item.to}
                end={item.to === '/dashboard'}
                viewTransition
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive ? 'bg-card text-text' : 'text-text/50 group-hover:text-text'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      icon={item.icon}
                      size={20}
                      className={cn('transition-colors', isActive ? 'text-accent' : 'text-accent/50 group-hover:text-accent')}
                    />
                    <span className='text-md'>{item.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className='mt-2 border-t border-border/30'>
          <button
            onClick={() => activeConversationId ? openChat(activeConversationId) : startNewConversation()}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full hover:cursor-pointer mt-3',
              chatOpen ? 'bg-card text-text' : 'text-text/50 hover:text-text',
            )}
          >
            <Icon
              icon={IconKey.Sparkles}
              size={20}
              className={cn('transition-colors', chatOpen ? 'text-accent' : 'text-accent/50')}
            />
            <span className='text-md'>Ask Tally</span>
          </button>
        </div>
        <div className='mt-2 border-t border-border/30'>
          <NotificationBell />
        </div>
      </nav>

      {/* Profile dropdown */}
      <div className='mx-auto px-1 mb-4 w-fit min-w-[220px] max-w-[220px] border border-accent/20 rounded-sm bg-card shadow-lg'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className='flex w-full items-center gap-3 rounded-sm p-2 text-left hover:bg-secondary/30 transition-colors outline-none hover:cursor-pointer'>
              <Avatar className='h-8 w-8 shrink-0'>
                {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user.name} />}
                <AvatarFallback
                  style={user?.avatar_color ? { backgroundColor: user.avatar_color } : undefined}
                  className='text-white text-xs font-medium'
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-text truncate'>{user?.name}</p>
                <p className='text-xs text-text/50 truncate'>{user?.email}</p>
              </div>
              <Icon icon={IconKey.ChevronsUpDown} size={16} className='shrink-0 text-text/50' />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side='top' align='start' className='w-(--radix-dropdown-menu-trigger-width)'>
            <DropdownMenuItem onSelect={() => setSettingsOpen(true)}>
              <Icon icon={IconKey.Settings} size={16} />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => { const next = isDark ? 'light' : 'dark'; setTheme(next); updateThemePref(next); }}>
              <Icon icon={isDark ? IconKey.Sun : IconKey.Moon} size={16} />
              {isDark ? 'Light mode' : 'Dark mode'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => logout()}>
              <Icon icon={IconKey.LogOut} size={16} />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </aside>
  );
}
